import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Dialog, DialogContent, Typography, Button, IconButton,
  Chip, Grid, Card, CardActionArea, ToggleButton, ToggleButtonGroup,
  TextField, MenuItem, Select, FormControl, LinearProgress,
  Fade, Zoom, Slide, Paper, Divider, Stack
} from "@mui/material";
import { createTheme, ThemeProvider, alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// ── THEME ──────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#0d0d0d", contrastText: "#fff" },
    secondary: { main: "#2563eb" },
    error: { main: "#dc2626" },
    success: { main: "#16a34a" },
    background: { default: "#f5f5f5", paper: "#ffffff" },
    text: { primary: "#171717", secondary: "#737373" },
    grey: {
      50: "#fafafa", 100: "#f5f5f5", 200: "#e5e5e5",
      300: "#d4d4d4", 400: "#a3a3a3", 500: "#737373",
      700: "#404040", 900: "#171717"
    },
  },
  typography: {
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    h2: { fontSize: 22, fontWeight: 700, letterSpacing: -0.5, lineHeight: 1.3 },
    body1: { fontSize: 14, lineHeight: 1.55 },
    body2: { fontSize: 13, lineHeight: 1.5 },
    caption: { fontSize: 11, letterSpacing: 0.3 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none", fontWeight: 600, fontSize: 14,
          borderRadius: 100, letterSpacing: -0.1, transition: "all 0.15s",
          "&:active": { transform: "scale(0.98)" },
        },
        containedPrimary: {
          background: "#0d0d0d", color: "#fff", padding: "13px 20px",
          "&:hover": { background: "#2a2a2a", boxShadow: "none" },
        },
      },
      defaultProps: { disableElevation: true },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16, maxHeight: "92vh",
          boxShadow: "0 24px 80px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.06)",
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 100, fontWeight: 600, fontSize: 11 } },
    },
  },
});

// ── CONSTANTS ──────────────────────────────────────────────────────────────
const SCREENS = {
  S1: "screen1", S2: "screen2", SETUP: "setup_offer",
  S3: "screen3", PRICING: "screen3_pricing", S4: "screen4",
  CAL: "calendar",
  RESCUED: "s_rescued", PAUSED: "s_paused", EXTENDED: "s_extended",
  DOWNGRADED: "s_downgraded", CANCELLED: "s_cancelled",
};

const PLAN_DATA = {
  launch: { name: "Launch", sub: "50 Live Attendees", m: 44, a: 22 },
  pro:    { name: "Pro",    sub: "200 Live + 1k Auto", m: 116, a: 58 },
  growth: { name: "Growth", sub: "500 Live + 2.5k Auto", m: 198, a: 99 },
};

const RESCUE_MAP = {
  performance: { text: "A funnel review takes 30 minutes. Most users find at least one major fix they hadn't tried.", cta: "Book My Free Funnel Audit" },
  notlaunched: { text: "Your webinar is ready to go. Take 30 more days — on us — to launch it.", cta: "Get 30 Days Free to Launch" },
  notime:      { text: "Pause for $9.99/mo. Your webinars keep running on autopilot — nothing for you to manage.", cta: "Pause My Account" },
  technical:   { text: "One call with our tech team fixes most issues in under an hour — completely free.", cta: "Schedule Tech Support Call" },
  expensive:   { text: "Claim 30% off your current plan right now. Same features, lower price, no commitment.", cta: "Claim 30% Off Monthly" },
  other:       { text: "A real person will read your feedback and follow up within 1 business day.", cta: "Send My Feedback" },
  "":          { text: "Before you go, let us offer you one last option. Our team can help you get results.", cta: "Talk to Our Team" },
};

const AVAIL_DAYS = [11, 12, 13, 16, 17, 18, 19, 20, 23, 24, 25, 26, 27];
const SLOTS = ["9:00 AM","10:00 AM","11:00 AM","1:00 PM","2:00 PM","3:00 PM","4:00 PM"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_LABELS = ["Su","Mo","Tu","We","Th","Fr","Sa"];

// ── SMALL REUSABLE PIECES ─────────────────────────────────────────────────
const ProgressDots = ({ total = 4, current }: { total?: number; current: number }) => (
  <Box sx={{ display: "flex", gap: "5px", justifyContent: "center", mb: 2.5 }}>
    {Array.from({ length: total }).map((_, i) => (
      <Box key={i} sx={{
        height: 6, borderRadius: 3,
        width: i === current ? 18 : 6,
        background: i < current ? "#a3a3a3" : i === current ? "#171717" : "#e5e5e5",
        transition: "all 0.3s",
      }} />
    ))}
  </Box>
);

const DarkBtn = ({ children, onClick, disabled = false, sx = {} }: { children: React.ReactNode; onClick?: () => void; disabled?: boolean; sx?: object }) => (
  <Button fullWidth variant="contained" onClick={onClick} disabled={disabled}
    sx={{ py: 1.65, ...sx }}>
    {children}
  </Button>
);

const OutlineBtn = ({ children, onClick, sx = {} }: { children: React.ReactNode; onClick?: () => void; sx?: object }) => (
  <Button fullWidth variant="outlined" onClick={onClick}
    sx={{ py: 1.6, borderRadius: 100, fontWeight: 600, fontSize: 14,
      borderColor: "#d4d4d4", color: "#171717", letterSpacing: -0.1,
      "&:hover": { borderColor: "#a3a3a3", background: "#fafafa" }, ...sx }}>
    {children}
  </Button>
);

const RedOutlineBtn = ({ children, onClick, sx = {} }: { children: React.ReactNode; onClick?: () => void; sx?: object }) => (
  <Button fullWidth variant="outlined" onClick={onClick}
    sx={{ py: 1.6, borderRadius: 100, fontWeight: 600, fontSize: 14,
      borderColor: "#fca5a5", color: "#dc2626", letterSpacing: -0.1,
      "&:hover": { borderColor: "#f87171", background: "#fef2f2" }, ...sx }}>
    {children}
  </Button>
);

const SkipLink = ({ children, onClick, red }: { children: React.ReactNode; onClick?: () => void; red?: boolean }) => (
  <Button fullWidth onClick={onClick}
    sx={{ color: red ? "#dc2626" : "#737373", fontWeight: 500, fontSize: 14,
      py: 1.2, "&:hover": { color: red ? "#b91c1c" : "#171717", background: "transparent" } }}>
    {children}
  </Button>
);

const SectionPad = ({ children, noPb }: { children: React.ReactNode; noPb?: boolean }) => (
  <Box sx={{ px: 3.5, pt: 3.5, pb: noPb ? 0 : 3.5 }}>{children}</Box>
);

const ModalTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="h2" sx={{ mb: 0.75 }}>{children}</Typography>
);

const ModalSub = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ fontSize: 14, color: "text.secondary", lineHeight: 1.55 }}>{children}</Typography>
);

// ── DASHBOARD BACKGROUND ─────────────────────────────────────────────────
const DashBg = () => {
  const stats = [
    { label: "Registrants", value: "2,481" },
    { label: "Active Webinars", value: "3" },
    { label: "Conversion Rate", value: "14.2%" },
    { label: "Last Event", value: "Mar 8" },
    { label: "Emails Sent", value: "8,920" },
    { label: "Revenue", value: "$4,280" },
  ];
  return (
    <Box sx={{ position: "fixed", inset: 0, background: "#fff", zIndex: 0, display: "flex", flexDirection: "column" }}>
      <Box sx={{ height: 56, borderBottom: "1px solid #e5e5e5", display: "flex", alignItems: "center", px: 3, gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 26, height: 26, background: "#0d0d0d", borderRadius: "7px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography sx={{ color: "#fff", fontSize: 11, fontWeight: 700, lineHeight: 1 }}>EW</Typography>
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: 15 }}>EasyWebinar</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.25, ml: 2 }}>
          {["Dashboard","Webinars","Analytics","Settings"].map((l, i) => (
            <Typography key={l} sx={{ px: 1.5, py: 0.625, borderRadius: "7px", fontSize: 13,
              background: i === 0 ? "#f5f5f5" : "transparent",
              color: i === 0 ? "#171717" : "#737373",
              fontWeight: i === 0 ? 500 : 400 }}>{l}</Typography>
          ))}
        </Box>
      </Box>
      <Grid container spacing={1.75} sx={{ p: 3.5, opacity: 0.3, filter: "blur(1.5px)", flex: 1 }}>
        {stats.map(s => (
          <Grid item xs={4} key={s.label}>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: "12px" }}>
              <Typography sx={{ fontSize: 12, color: "#a3a3a3", mb: 1 }}>{s.label}</Typography>
              <Typography sx={{ fontSize: 26, fontWeight: 600 }}>{s.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// ── LOSS GRID ─────────────────────────────────────────────────────────────
const LossGrid = () => {
  const items = [
    { icon: "⚙️", title: "Webinar settings", desc: "All configurations deleted" },
    { icon: "🎬", title: "Recorded sessions", desc: "All recordings removed" },
    { icon: "👥", title: "Registrant data",   desc: "Analytics lost forever" },
    { icon: "🔗", title: "CRM integrations",  desc: "All connections removed" },
  ];
  return (
    <Grid container spacing={1} sx={{ mt: 2 }}>
      {items.map(item => (
        <Grid item xs={6} key={item.title}>
          <Box sx={{ p: 1.75, border: "1px solid #e5e5e5", borderRadius: "10px", background: "#fafafa" }}>
            <Typography sx={{ fontSize: 16, mb: 0.75 }}>{item.icon}</Typography>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, mb: 0.25 }}>{item.title}</Typography>
            <Typography sx={{ fontSize: 11.5, color: "text.secondary", lineHeight: 1.35 }}>{item.desc}</Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

// ── RESCUE CARD ──────────────────────────────────────────────────────────
const RescueCard = ({ reason, goTo }: { reason: string; goTo: (s: string) => void }) => {
  const configs = {
    performance: {
      icon: "📊", title: "Free Webinar Funnel Audit", tag: "FREE", tagBg: "#2563eb",
      desc: "Our team reviews your funnel end-to-end — registration page, email sequence, CTA, and replay. 30 minutes, no pitch.",
      btnLabel: "Book Free Funnel Audit", target: SCREENS.RESCUED,
      border: "#bfdbfe", bg: "#eff6ff",
    },
    notlaunched: {
      icon: "🚀", title: "Free 7-Day Launch Sprint", tag: "LAUNCH IN 7 DAYS", tagBg: "#16a34a",
      desc: "We'll get your first webinar live in 7 days — guaranteed. Our team handles setup, tech, and your first promotion.",
      btnLabel: "Get Free Launch Help", target: SCREENS.RESCUED,
      border: "#bbf7d0", bg: "#f0fdf4",
    },
    notime: {
      icon: "⏸", title: "Pause for $9.99/month", tag: "PAUSE", tagBg: "#f97316",
      desc: "Keep everything safe at $9.99/mo. Your webinars run on autopilot — nothing for you to manage.",
      btnLabel: "Pause My Account", target: SCREENS.PAUSED,
      border: "#fed7aa", bg: "#fff7ed",
    },
    technical: {
      icon: "🔧", title: "Priority Tech Support Call", tag: "24HR RESPONSE", tagBg: "#d97706",
      desc: "Jump on a call with our tech team within 24 hours. We'll diagnose and resolve the issues you experienced.",
      btnLabel: "Schedule Support Call", target: SCREENS.RESCUED,
      border: "#fde68a", bg: "#fffbeb",
    },
    other: {
      icon: "💬", title: "We'll follow up personally", tag: "HUMAN RESPONSE", tagBg: "#2563eb",
      desc: "A real person reads every response. If there's something we can fix or help with — we'll reach out within 1 business day.",
      btnLabel: "Send My Feedback", target: SCREENS.RESCUED,
      border: "#bfdbfe", bg: "#eff6ff",
    },
  };

  if (reason === "expensive") {
    return (
      <Box sx={{ border: "1.5px solid #e5e5e5", borderRadius: "10px", p: 2, mb: 1.75, animation: "fadeUp 0.22s ease" }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1.25 }}>Flexible options for your budget</Typography>
        {[
          { icon: "⭐", title: "Switch to annual — 50% off", desc: "Half the price, locked in for the year", target: SCREENS.PRICING },
          { icon: "🎁", title: "Stay monthly with 30% off",  desc: "Same plan, lower rate, no commitment",  target: SCREENS.RESCUED },
          { icon: "⏸", title: "Pause for $9.99/month",       desc: "Keep data safe, resume anytime",        target: SCREENS.PAUSED },
        ].map((opt, i) => (
          <Box key={i} onClick={() => goTo(opt.target)}
            sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, border: "1.5px solid #e5e5e5",
              borderRadius: "10px", cursor: "pointer", background: "#fff", mb: i < 2 ? 1 : 0,
              transition: "all .15s", "&:hover": { borderColor: "#a3a3a3", background: "#fafafa" } }}>
            <Typography sx={{ fontSize: 18 }}>{opt.icon}</Typography>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{opt.title}</Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>{opt.desc}</Typography>
            </Box>
            <ChevronRightIcon sx={{ color: "#a3a3a3", fontSize: 18 }} />
          </Box>
        ))}
      </Box>
    );
  }

  const c = configs[reason as keyof typeof configs];
  if (!c) return null;
  return (
    <Box sx={{ border: `1.5px solid ${c.border}`, borderRadius: "10px", p: 2, mb: 1.75,
      background: c.bg, animation: "fadeUp 0.22s ease" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
        <Typography sx={{ fontSize: 17 }}>{c.icon}</Typography>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{c.title}</Typography>
        <Chip label={c.tag} size="small" sx={{ ml: "auto", background: c.tagBg, color: "#fff",
          fontSize: 10, fontWeight: 700, height: 20, borderRadius: 100 }} />
      </Box>
      <Typography sx={{ fontSize: 12.5, color: "#404040", mb: 1.5, lineHeight: 1.5 }}>{c.desc}</Typography>
      <DarkBtn onClick={() => goTo(c.target)} sx={{ fontSize: 13, py: 1.4 }}>{c.btnLabel}</DarkBtn>
    </Box>
  );
};

// ── PLAN CARD ─────────────────────────────────────────────────────────────
const PlanCard = ({ planKey, pMode, selected, onSelect }: { planKey: keyof typeof PLAN_DATA; pMode: string; selected: keyof typeof PLAN_DATA | null; onSelect: (k: keyof typeof PLAN_DATA) => void }) => {
  const p = PLAN_DATA[planKey];
  const isM = pMode === "monthly";
  const featured = planKey === "pro";
  const price = isM ? Math.round(p.m * 0.7) : p.a;
  const saving = isM ? "Save 30%" : `Save $${(p.m - p.a) * 12}/yr`;
  const isSelected = selected === planKey;

  return (
    <Box onClick={() => onSelect(planKey)}
      sx={{
        border: isSelected ? "1.5px solid #2563eb" : featured ? "none" : "1.5px solid #e5e5e5",
        borderRadius: "10px", p: 1.75, cursor: "pointer",
        background: featured ? "#0d0d0d" : "#fff",
        transform: featured ? "translateY(-3px)" : "none",
        boxShadow: featured ? "0 8px 24px rgba(0,0,0,0.2)" : isSelected ? "0 0 0 3px rgba(37,99,235,0.1)" : "none",
        transition: "all .18s",
        "&:hover": { transform: featured ? "translateY(-5px)" : "translateY(-1px)", borderColor: featured ? "none" : isSelected ? "#2563eb" : "#a3a3a3" },
        position: "relative", overflow: "hidden",
      }}>
      {featured && (
        <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, textAlign: "center",
          fontSize: 10, fontWeight: 700, py: 0.4, background: "#2563eb", color: "#fff", letterSpacing: "0.04em" }}>
          MOST POPULAR
        </Box>
      )}
      <Box sx={{ mt: featured ? 2.5 : 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: featured ? "#fff" : "#171717" }}>{p.name}</Typography>
        <Typography sx={{ fontSize: 11, color: featured ? "rgba(255,255,255,0.65)" : "#737373", mt: 0.25 }}>{p.sub}</Typography>
        <Typography sx={{ fontSize: 11.5, color: featured ? "rgba(255,255,255,0.45)" : "#a3a3a3", textDecoration: "line-through", mt: 1.25 }}>${p.m}</Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: featured ? "#fff" : "#171717", letterSpacing: -0.4 }}>${price}</Typography>
        <Typography sx={{ fontSize: 11, color: featured ? "rgba(255,255,255,0.65)" : "#737373" }}>/mo</Typography>
        <Box sx={{ display: "inline-block", mt: 0.75, fontSize: 11, fontWeight: 600,
          color: featured ? "#4ade80" : "#16a34a",
          background: featured ? "rgba(34,197,94,0.2)" : "#f0fdf4",
          px: 1, py: 0.25, borderRadius: 100 }}>
          {saving}
        </Box>
      </Box>
    </Box>
  );
};

// ── CALENDAR ──────────────────────────────────────────────────────────────
const CalendarPicker = ({ onConfirm }: { onConfirm: () => void }) => {
  const [calY, setCalY] = useState(2026);
  const [calM, setCalM] = useState(2);
  const [selDate, setSelDate] = useState<number | null>(null);
  const [selSlot, setSelSlot] = useState<string | null>(null);
  const TODAY = 10;

  const changeMonth = (d: number) => {
    let nm = calM + d, ny = calY;
    if (nm > 11) { nm = 0; ny++; }
    if (nm < 0) { nm = 11; ny--; }
    setCalM(nm); setCalY(ny); setSelDate(null); setSelSlot(null);
  };

  const firstDay = new Date(calY, calM, 1).getDay();
  const daysInMonth = new Date(calY, calM + 1, 0).getDate();

  const dateLabel = selDate
    ? new Date(calY, calM, selDate).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
    : null;

  return (
    <Box>
      {/* Month header */}
      <Box sx={{ border: "1.5px solid #e5e5e5", borderRadius: "10px", overflow: "hidden", mt: 2.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, borderBottom: "1px solid #e5e5e5" }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{MONTHS[calM]} {calY}</Typography>
          <Box sx={{ display: "flex", gap: 0.75 }}>
            {([[-1, "‹"], [1, "›"]] as [number, string][]).map(([d, icon]) => (
              <IconButton key={d} onClick={() => changeMonth(d)} size="small"
                sx={{ width: 26, height: 26, border: "1px solid #e5e5e5", borderRadius: "6px", fontSize: 14 }}>
                {icon}
              </IconButton>
            ))}
          </Box>
        </Box>
        {/* Grid */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", px: 1.5, pb: 1 }}>
          {DAY_LABELS.map(d => (
            <Typography key={d} sx={{ textAlign: "center", fontSize: 11, fontWeight: 600, color: "#a3a3a3", py: 0.75 }}>{d}</Typography>
          ))}
          {Array.from({ length: firstDay }).map((_, i) => <Box key={`e${i}`} />)}
          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => {
            const isAvail = AVAIL_DAYS.includes(d) && d >= TODAY;
            const isToday = d === TODAY;
            const isSel = selDate === d;
            return (
              <Box key={d} onClick={() => isAvail && setSelDate(d)}
                sx={{
                  aspectRatio: "1", display: "flex", alignItems: "center", justifyContent: "center",
                  borderRadius: "8px", fontSize: 13,
                  background: isSel ? "#0d0d0d" : "transparent",
                  color: isSel ? "#fff" : isToday ? "#2563eb" : isAvail ? "#171717" : "#d4d4d4",
                  fontWeight: isSel || isToday ? 700 : isAvail ? 500 : 400,
                  cursor: isAvail ? "pointer" : "default",
                  transition: "background .12s",
                  "&:hover": isAvail && !isSel ? { background: "#f5f5f5" } : {},
                }}>
                {d}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Time slots */}
      {selDate && (
        <Fade in>
          <Box sx={{ mt: 1.75 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#737373", textTransform: "uppercase", letterSpacing: "0.04em", mb: 1 }}>
              Available times — {dateLabel}
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "7px" }}>
              {SLOTS.map(t => {
                const isSel = selSlot === t;
                return (
                  <Box key={t} onClick={() => setSelSlot(t)}
                    sx={{
                      py: 1.15, px: 1.5, border: `1.5px solid ${isSel ? "#0d0d0d" : "#e5e5e5"}`,
                      borderRadius: "10px", fontSize: 13, fontWeight: 500,
                      color: isSel ? "#fff" : "#404040", textAlign: "center", cursor: "pointer",
                      background: isSel ? "#0d0d0d" : "#fff",
                      transition: "all .12s", "&:hover": !isSel ? { borderColor: "#a3a3a3", background: "#fafafa" } : {},
                    }}>
                    {t} EST
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Fade>
      )}

      {/* Summary */}
      {selDate && selSlot && (
        <Fade in>
          <Box sx={{ mt: 1.5, background: "#f0fdf4", border: "1.5px solid #bbf7d0", borderRadius: "10px",
            px: 2, py: 1.4, display: "flex", alignItems: "center", gap: 1.25 }}>
            <Typography sx={{ fontSize: 16 }}>✅</Typography>
            <Typography sx={{ fontSize: 13, color: "#166534", fontWeight: 500 }}>
              {dateLabel} at {selSlot} EST — 60-min Complimentary Setup Session
            </Typography>
          </Box>
        </Fade>
      )}

      <Box sx={{ mt: 2, mb: 0 }}>
        <DarkBtn disabled={!selDate || !selSlot} onClick={onConfirm} sx={{ mb: 1 }}>
          Confirm My Free Setup Call →
        </DarkBtn>
      </Box>
    </Box>
  );
};

// ── SUCCESS SCREEN ─────────────────────────────────────────────────────────
const SuccessScreen = ({ icon, title, sub, onBack }: { icon: React.ReactNode; title: string; sub: string; onBack: () => void }) => (
  <Box sx={{ textAlign: "center", pt: 4.5, px: 3.5, pb: 3.5 }}>
    <Box sx={{
      width: 60, height: 60, borderRadius: "50%", border: "2px solid #e5e5e5",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 26, mx: "auto", mb: 2.25,
      animation: "popIn 0.4s cubic-bezier(0.34,1.56,0.64,1)",
      "@keyframes popIn": { from: { transform: "scale(0)", opacity: 0 }, to: { transform: "scale(1)", opacity: 1 } }
    }}>
      {icon}
    </Box>
    <Typography variant="h2" sx={{ mb: 0.75 }}>{title}</Typography>
    <Typography sx={{ fontSize: 14, color: "text.secondary", lineHeight: 1.55, mb: 3 }}>{sub}</Typography>
    <DarkBtn onClick={onBack}>← Back to Dashboard</DarkBtn>
  </Box>
);

interface CancellationFlowProps {
  open: boolean;
  onClose: () => void;
}

// ── MAIN COMPONENT ─────────────────────────────────────────────────────────
export default function CancellationFlow({ open, onClose }: CancellationFlowProps) {
  const [screen, setScreen] = useState(SCREENS.S1);
  const [reason, setReason] = useState("");
  const [pMode, setPMode] = useState("monthly");
  const [selectedPlan, setSelectedPlan] = useState<keyof typeof PLAN_DATA | null>(null);

  const goTo = useCallback((s: string) => setScreen(s), []);

  const restart = () => {
    setReason("");
    setPMode("monthly");
    setSelectedPlan(null);
    goTo(SCREENS.S1);
    onClose();
  };

  const rescueData = RESCUE_MAP[reason as keyof typeof RESCUE_MAP] || RESCUE_MAP[""];
  const isModalScreen = Object.values(SCREENS).includes(screen);

  const planDisc = selectedPlan
    ? (pMode === "monthly" ? Math.round(PLAN_DATA[selectedPlan].m * 0.7) : PLAN_DATA[selectedPlan].a)
    : null;

  const renderScreen = () => {
    switch (screen) {

      // ── SCREEN 1 ──────────────────────────────────────────────────────
      case SCREENS.S1:
        return (
          <Box>
            <SectionPad noPb>
              <Typography variant="h2">Before you cancel…</Typography>
              <ModalSub>You'll permanently lose everything below. This can't be undone.</ModalSub>
              <LossGrid />

              {/* Pause card */}
              <Box sx={{ border: "1.5px solid #e5e5e5", borderRadius: "10px", p: 2, background: "#fafafa", mt: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: 16 }}>⏸</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Pause instead</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>$9.99<Typography component="span" sx={{ fontSize: 12, fontWeight: 400, color: "text.secondary" }}>/mo</Typography></Typography>
                </Box>
                <Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.5, mb: 1.5 }}>
                  Keep your account, data, and webinars safe. Resume in one click — no re-setup needed.
                </Typography>
                <DarkBtn onClick={() => goTo(SCREENS.PAUSED)}>Pause My Account</DarkBtn>
              </Box>
            </SectionPad>

            <Box sx={{ px: 3.5, pt: 1.75, pb: 3.5 }}>
              {/* Offer pill */}
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1.75 }}>
                <Box onClick={() => goTo(SCREENS.SETUP)} sx={{
                  display: "inline-flex", alignItems: "center", gap: 1,
                  py: "7px", pl: "8px", pr: "14px", borderRadius: 100,
                  border: "1.5px solid #e5e5e5", background: "#fff", cursor: "pointer",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)", transition: "all .15s",
                  "&:hover": { borderColor: "#a3a3a3", boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }
                }}>
                  <Chip label="⚡ FREE SETUP" size="small"
                    sx={{ background: "#0d0d0d", color: "#fff", fontSize: 10.5, fontWeight: 700, borderRadius: 100, height: 22 }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 500 }}>Up to 50% off — explore plans</Typography>
                  <Typography sx={{ fontSize: 13, color: "#a3a3a3" }}>→</Typography>
                </Box>
              </Box>
              <SkipLink red onClick={() => goTo(SCREENS.S2)}>Cancel subscription</SkipLink>
            </Box>
          </Box>
        );

      // ── SCREEN 2 ──────────────────────────────────────────────────────
      case SCREENS.S2:
        return (
          <Box>
            <SectionPad noPb>
              <ProgressDots total={4} current={1} />
              <ModalTitle>What would you like to do?</ModalTitle>
              <ModalSub>Choose the option that best fits where you are right now.</ModalSub>
              <Stack spacing={1} sx={{ mt: 2.5 }}>
                {[
                  { icon: "💳", label: "Explore lower-cost plans", desc: "Monthly 30% off or annual 50% off · free setup included", badge: "UP TO 50% OFF", target: SCREENS.SETUP },
                  { icon: "⬇️", label: "Downgrade to Free", desc: "Keep basic features · upgrade anytime", badge: null, target: SCREENS.DOWNGRADED },
                  { icon: "✕",  label: "Cancel subscription", desc: "Lose all data and premium features permanently", badge: null, target: SCREENS.S3 },
                ].map(item => (
                  <Box key={item.label} onClick={() => goTo(item.target)}
                    sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                      p: "13px 16px", border: "1.5px solid #e5e5e5", borderRadius: "10px",
                      cursor: "pointer", background: "#fff", transition: "all .15s",
                      "&:hover": { borderColor: "#a3a3a3", background: "#fafafa" } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Typography sx={{ fontSize: 17, width: 22, textAlign: "center" }}>{item.icon}</Typography>
                      <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{item.label}</Typography>
                        <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.15 }}>{item.desc}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                      {item.badge && <Chip label={item.badge} size="small"
                        sx={{ background: "#0d0d0d", color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: 100, height: 20 }} />}
                      <Typography sx={{ color: "#a3a3a3", fontSize: 14 }}>→</Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </SectionPad>
            <Box sx={{ px: 3.5, pt: 2.5, pb: 3.5 }}>
              <OutlineBtn onClick={() => goTo(SCREENS.S1)}>← Go Back</OutlineBtn>
            </Box>
          </Box>
        );

      // ── SETUP OFFER ───────────────────────────────────────────────────
      case SCREENS.SETUP:
        return (
          <Box>
            <SectionPad noPb>
              <Chip label="🎁 Up to 50% off + Free Setup Session" variant="outlined"
                sx={{ mb: 1.5, fontSize: 12, borderRadius: 100, border: "1px solid #d4d4d4", color: "#404040" }} />
              <ModalTitle>Let's find a plan{"\n"}that works for you</ModalTitle>
              <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 0.75, lineHeight: 1.55, mb: 2 }}>
                We'll personally review your account, match you to the right plan, and get your webinar live together. A real person, one call.
              </Typography>

              <Box sx={{ border: "1px solid #e5e5e5", borderRadius: "10px", overflow: "hidden" }}>
                {[
                  { icon: "🎯", title: <>Custom discount — up to <span style={{ color: "#2563eb" }}>50% off</span></>, desc: "Matched to your usage and situation, not a generic promo code" },
                  { icon: "🚀", title: "Free 1-on-1 setup session", desc: "We configure your webinar, emails, and CTA live on the call — end to end" },
                  { icon: "⏱",  title: "30-minute call", desc: "First available slot typically within 24 hours" },
                ].map((row, i, arr) => (
                  <Box key={i} sx={{ display: "flex", gap: 1.5, p: "12px 14px",
                    borderBottom: i < arr.length - 1 ? "1px solid #f5f5f5" : "none" }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: "8px", background: "#f5f5f5",
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>
                      {row.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{row.title}</Typography>
                      <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25, lineHeight: 1.4 }}>{row.desc}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Stats */}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", border: "1px solid #e5e5e5",
                borderRadius: "10px", overflow: "hidden", mt: 2 }}>
                {[
                  { num: "94%", lbl: "find a plan that works" },
                  { num: "<24hr", lbl: "first slot available" },
                  { num: "30 min", lbl: "No pitch · Real help" },
                ].map((s, i) => (
                  <Box key={i} sx={{ py: 1.5, px: 1, textAlign: "center", background: "#fff",
                    borderLeft: i > 0 ? "1px solid #e5e5e5" : "none" }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: -0.3 }}>{s.num}</Typography>
                    <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.25, lineHeight: 1.35 }}>{s.lbl}</Typography>
                  </Box>
                ))}
              </Box>
            </SectionPad>

            <Box sx={{ px: 3.5, pt: 2.5, pb: 3.5 }}>
              <DarkBtn onClick={() => goTo(SCREENS.PRICING)} sx={{ mb: 1 }}>📅 Explore Plans + Book Free Call →</DarkBtn>
              <Typography sx={{ textAlign: "center", fontSize: 12, color: "#a3a3a3", mb: 1.75 }}>No commitment · Takes 30 seconds to explore</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <OutlineBtn onClick={() => goTo(SCREENS.S1)}>← Go Back</OutlineBtn>
                <RedOutlineBtn onClick={() => goTo(SCREENS.S3)}>Cancel Anyway</RedOutlineBtn>
              </Box>
            </Box>
          </Box>
        );

      // ── SCREEN 3 (Reason) ─────────────────────────────────────────────
      case SCREENS.S3:
        return (
          <Box>
            <SectionPad noPb>
              <ProgressDots total={4} current={2} />
              <ModalTitle>Before you go —{"\n"}can we help?</ModalTitle>
              <ModalSub>Tell us what's not working. We may have a fix you haven't tried.</ModalSub>

              <Box sx={{ mt: 2.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#737373", letterSpacing: "0.03em", textTransform: "uppercase", mb: 0.875 }}>
                  Main reason for leaving
                </Typography>
                <Select fullWidth value={reason} onChange={(e) => setReason(e.target.value)}
                  displayEmpty renderValue={reason ? undefined : () => "Select a reason…"}
                  sx={{ fontSize: 14, borderRadius: "10px", mb: 1.75,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e5e5", borderWidth: "1.5px" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a3a3a3" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#a3a3a3", borderWidth: "1.5px" }
                  }}>
                  <MenuItem value="">Select a reason…</MenuItem>
                  <MenuItem value="performance">My webinar didn't get the results I wanted</MenuItem>
                  <MenuItem value="notlaunched">I never got my webinar launched</MenuItem>
                  <MenuItem value="notime">I don't have time right now</MenuItem>
                  <MenuItem value="technical">I had technical problems</MenuItem>
                  <MenuItem value="expensive">The price doesn't fit my budget</MenuItem>
                  <MenuItem value="other">Something else</MenuItem>
                </Select>

                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#737373", letterSpacing: "0.03em", textTransform: "uppercase", mb: 0.875 }}>
                  Additional feedback <Typography component="span" sx={{ textTransform: "none", fontWeight: 400, color: "#a3a3a3" }}>(optional)</Typography>
                </Typography>
                <TextField fullWidth multiline rows={3} placeholder="Tell us more…" variant="outlined"
                  sx={{ mb: 1.75,
                    "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: 13.5,
                      "& fieldset": { borderColor: "#e5e5e5", borderWidth: "1.5px" },
                      "&:hover fieldset": { borderColor: "#a3a3a3" },
                      "&.Mui-focused fieldset": { borderColor: "#a3a3a3", borderWidth: "1.5px" }
                    }
                  }} />

                {reason && <RescueCard reason={reason} goTo={goTo} />}
              </Box>
            </SectionPad>
            <Box sx={{ px: 3.5, pt: 2, pb: 3.5 }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <OutlineBtn onClick={() => goTo(SCREENS.S2)}>← Go Back</OutlineBtn>
                <RedOutlineBtn onClick={() => goTo(SCREENS.S4)}>Cancel Subscription</RedOutlineBtn>
              </Box>
            </Box>
          </Box>
        );

      // ── PRICING ───────────────────────────────────────────────────────
      case SCREENS.PRICING:
        return (
          <Box>
            <SectionPad noPb>
              <ProgressDots total={4} current={2} />
              <ModalTitle>Explore lower-cost plans</ModalTitle>
              <ModalSub>Same full platform — pick the billing that fits your budget.</ModalSub>

              {/* Toggle */}
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2.25, mb: 2.25 }}>
                <Box sx={{ display: "flex", background: "#f5f5f5", borderRadius: 100, p: "3px", gap: 0.25 }}>
                  {[["monthly","Monthly","30% OFF"], ["annual","Annual","50% OFF"]].map(([val, label, tag]) => (
                    <Box key={val} onClick={() => { setPMode(val); setSelectedPlan(null); }}
                      sx={{
                        px: 2.25, py: 0.75, borderRadius: 100, cursor: "pointer", fontSize: 13,
                        fontWeight: 500, display: "flex", alignItems: "center", gap: 0.75,
                        transition: "all .2s",
                        background: pMode === val ? "#fff" : "transparent",
                        color: pMode === val ? "#171717" : "#737373",
                        boxShadow: pMode === val ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                      }}>
                      {label}
                      <Box sx={{ fontSize: 10, fontWeight: 700, px: 0.875, py: 0.125, borderRadius: 100,
                        background: pMode === val ? "#0d0d0d" : "#e5e5e5",
                        color: pMode === val ? "#fff" : "#737373", transition: "all .2s" }}>
                        {tag}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Plans */}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1, mb: 1.75 }}>
                {(["launch","pro","growth"] as const).map(k => (
                  <PlanCard key={k} planKey={k} pMode={pMode} selected={selectedPlan} onSelect={setSelectedPlan} />
                ))}
              </Box>

              {/* Confirm */}
              {selectedPlan ? (
                <Fade in>
                  <Box sx={{ border: "1.5px solid #e5e5e5", borderRadius: "10px", px: 2, py: 1.625, mb: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: "8px", background: "#f0fdf4", border: "1px solid #bbf7d0",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✓</Box>
                    <Box>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                        {PLAN_DATA[selectedPlan].name} {pMode === "monthly" ? "Monthly" : "Annual"} selected
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.15 }}>
                        ${planDisc ?? 0}/mo · {pMode === "monthly" ? "30% off" : `billed $${(planDisc ?? 0) * 12}/yr`}
                      </Typography>
                    </Box>
                  </Box>
                </Fade>
              ) : (
                <Box sx={{ border: "1.5px dashed #e5e5e5", borderRadius: "10px", py: 1.75, textAlign: "center", mb: 1.5 }}>
                  <Typography sx={{ fontSize: 13, color: "#a3a3a3" }}>Select a plan above to see your savings</Typography>
                </Box>
              )}
            </SectionPad>

            <Box sx={{ px: 3.5, pt: 1.5, pb: 3.5 }}>
              <DarkBtn disabled={!selectedPlan} onClick={() => goTo(SCREENS.RESCUED)} sx={{ mb: 1.25, opacity: selectedPlan ? 1 : 0.35 }}>
                {selectedPlan ? `Switch to ${PLAN_DATA[selectedPlan].name} ${pMode === "monthly" ? "Monthly" : "Annual"} →` : "Select a plan to continue"}
              </DarkBtn>
              <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                <OutlineBtn onClick={() => goTo(SCREENS.SETUP)}>← Go Back</OutlineBtn>
                <RedOutlineBtn onClick={() => goTo(SCREENS.S4)}>Cancel Anyway</RedOutlineBtn>
              </Box>
            </Box>
          </Box>
        );

      // ── SCREEN 4 (Final) ──────────────────────────────────────────────
      case SCREENS.S4:
        return (
          <Box>
            <SectionPad noPb>
              <ProgressDots total={4} current={3} />
              <ModalTitle>One last offer{"\n"}before you go</ModalTitle>
              <ModalSub>This is only available here, right now.</ModalSub>

              <Box sx={{ mt: 2.5 }}>
                {/* Annual */}
                <Box sx={{ border: "1.5px solid #e5e5e5", borderRadius: "10px", p: 2, mb: 1.25 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Switch to Annual</Typography>
                    <Chip label="50% OFF" size="small"
                      sx={{ background: "#0d0d0d", color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: 100, height: 20 }} />
                  </Box>
                  <Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.5, mb: 1.5 }}>
                    Pay once for the year at half the monthly price. Same platform, all features — just a smarter way to pay.
                  </Typography>
                  <DarkBtn onClick={() => goTo(SCREENS.PRICING)}>Claim 50% Annual Deal →</DarkBtn>
                </Box>

                {/* Reason-specific */}
                <Box sx={{ border: "1.5px solid #e5e5e5", borderRadius: "10px", p: 1.75 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#a3a3a3", textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.75 }}>
                    Or try this instead
                  </Typography>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 500, mb: 1.25 }}>{rescueData.text}</Typography>
                  <OutlineBtn onClick={() => goTo(SCREENS.RESCUED)}>{rescueData.cta}</OutlineBtn>
                </Box>
              </Box>
            </SectionPad>
            <Box sx={{ px: 3.5, pt: 2, pb: 3.5 }}>
              <SkipLink red onClick={() => goTo(SCREENS.CANCELLED)}>No thanks, I still want to cancel</SkipLink>
            </Box>
          </Box>
        );

      // ── CALENDAR ──────────────────────────────────────────────────────
      case SCREENS.CAL:
        return (
          <Box>
            <SectionPad noPb>
              <Chip label="✓ 30 Days Free Applied  ·  🎁 Complimentary Session" variant="outlined"
                sx={{ mb: 1.75, fontSize: 12, borderRadius: 100, border: "1px solid #d4d4d4", color: "#404040" }} />
              <ModalTitle>Book your free{"\n"}setup call</ModalTitle>
              <ModalSub>Pick a time and we'll get your webinar live together. Your next 30 days are on us.</ModalSub>
              <CalendarPicker onConfirm={() => goTo(SCREENS.EXTENDED)} />
            </SectionPad>
            <Box sx={{ px: 3.5, pt: 1, pb: 3.5 }}>
              <OutlineBtn onClick={() => goTo(SCREENS.S1)}>← Go Back</OutlineBtn>
            </Box>
          </Box>
        );

      // ── SUCCESS STATES ─────────────────────────────────────────────────
      case SCREENS.RESCUED:
        return <SuccessScreen icon="✅" title="You're all set!" sub="Our team will reach out within 24 hours to confirm your session and get started." onBack={restart} />;
      case SCREENS.PAUSED:
        return <SuccessScreen icon="⏸" title="Account paused" sub="Your account is paused at $9.99/month. Data, webinars, and settings are safe. Resume anytime in one click." onBack={restart} />;
      case SCREENS.EXTENDED:
        return <SuccessScreen icon="🎉" title="You're booked!" sub="Your setup call is confirmed and your next 30 days are free. Check your email for the calendar invite." onBack={restart} />;
      case SCREENS.DOWNGRADED:
        return <SuccessScreen icon="⬇️" title="Plan updated" sub="You've been moved to the Free plan. Your webinars and existing data are intact. Upgrade anytime from settings." onBack={restart} />;
      case SCREENS.CANCELLED:
        return <SuccessScreen icon="👋" title="Subscription cancelled" sub="Your subscription has been cancelled. Access continues until the end of your billing period. Sorry to see you go." onBack={restart} />;

      default:
        return null;
    }
  };

  if (!open) return null;
  return (
    <ThemeProvider theme={theme}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');
        @keyframes fadeUp { from { opacity:0; transform:translateY(18px) scale(.98); } to { opacity:1; transform:translateY(0) scale(1); } }
      `}</style>

      <DashBg />

      <Box sx={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
        backdropFilter: "blur(6px)", zIndex: 1300, overflowY: "auto" }}>
        <Box sx={{ minHeight: "100%", display: "flex", alignItems: "center", justifyContent: "center", p: 2.5 }}>
          <Box sx={{ width: "100%", maxWidth: screen === SCREENS.PRICING ? 460 : screen === SCREENS.CAL ? 420 : 440,
            background: "#fff", borderRadius: "16px",
            boxShadow: "0 24px 80px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.06)",
            animation: "fadeUp 0.28s cubic-bezier(0.22,1,0.36,1)",
            position: "relative", flexShrink: 0
          }}>
          {/* Close button */}
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", top: 16, right: 16, width: 28, height: 28,
              background: "#f5f5f5", color: "#737373", fontSize: 14, zIndex: 2,
              "&:hover": { background: "#e5e5e5", color: "#171717" } }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>

          <Fade key={screen} in timeout={220}>
            <Box>{renderScreen()}</Box>
          </Fade>
          </Box>
        </Box>
      </Box>

    </ThemeProvider>
  );
}